// import $ from 'jquery';
import { requestJson } from '~/modules/legacyRequest.js';
import legacyEventHub, { legacyRoot } from '~/modules/legacyRuntime.js';

const AudioPlayer = {
    currentTrackData: {}, // gets updated through initTrack
    currentTrackVariantId: null,
    currentTrackVariantData: null,
    currentPlaybackTrackData: null,
    currentPlaybackTrackVariantId: null,
    currentPlaybackTrackVariantData: null,
    currentPlaybackSeekRatio: null,
    visualiser : undefined,
    player: {
        width : parseInt(document.getElementById('player_wrapper')?.getAttribute('data-width') || '0', 10) || 0
    }, // mediaelement.js object
    mediaElement: null,
    playing : false,
    mounted: false,
    listenersBound: false,
    hasUserDrivenPlaybackSelection: false,
    currentRequestId: 0,
    currentMetadataRequest: null,
    currentWaveformToken: 0,
    pendingSeekRatio: null,
    resetProgressOnNextPlay: false,
    switchingPlaybackSource: false,
    ignoreNextTimeUpdateAfterSourceSwitch: false,
    playerVisible: false,
    mediaElementPlayerLoader: null,
    playerInitializationPromise: null,

    init: function () {
        legacyEventHub.on('mountAudioPlayer', this.mount.bind(this));

        legacyEventHub.on('playerInitTrack', function(id) {
            this.initTrack(id);
        }.bind(this));
    },

    mount: function() {
        if (this.mounted) {
            return;
        }

        this.mounted = true;
        this.create_waveform_preload_container();
        Promise.resolve(this.initPlayer()).catch(function(error) {
            console.error('AudioPlayer init failed', error);
        });
        this.addEventListeners();
    },

    showPlayer: function() {
        if (this.playerVisible) {
            return;
        }

        this.playerVisible = true;
        if (document.body) {
            document.body.classList.add('audio-player-visible');
        }
        Array.prototype.forEach.call(document.querySelectorAll('.player'), function(playerElement) {
            playerElement.classList.remove('player--inactive');
            playerElement.classList.add('player--active');
            playerElement.removeAttribute('inert');
            playerElement.removeAttribute('aria-hidden');
        });
    },

    create_waveform_preload_container : function() {
      if (document.getElementById('waveform_preload_container')) {
          return;
      }

      var playerWrapper = document.getElementById('player_wrapper');
      if (!playerWrapper) {
          return;
      }

      var preloadContainer = document.createElement('div');
      preloadContainer.id = 'waveform_preload_container';
      preloadContainer.style.overflow = 'hidden';
      preloadContainer.style.width = '0';
      preloadContainer.style.height = '0';
      playerWrapper.appendChild(preloadContainer);
    },

    resolveMediaElementPlayer: function() {
        var root = legacyRoot();
        return root.MediaElementPlayer || null;
    },

    loadMediaElementPlayerModule: function() {
        return import('../../assets/javascripts/shared/libs/mediaelementplayer.js');
    },

    ensureMediaElementPlayer: function() {
        var existingPlayerClass = this.resolveMediaElementPlayer();
        var that = this;

        if (existingPlayerClass) {
            return Promise.resolve(existingPlayerClass);
        }

        if (!this.mediaElementPlayerLoader) {
            this.mediaElementPlayerLoader = this.loadMediaElementPlayerModule().then(function(module) {
                var loadedPlayerClass = that.resolveMediaElementPlayer() || module.default || module.MediaElementPlayer || null;

                if (!loadedPlayerClass) {
                    throw new Error('MediaElementPlayer failed to load');
                }

                legacyRoot().MediaElementPlayer = loadedPlayerClass;
                return loadedPlayerClass;
            }).catch(function(error) {
                that.mediaElementPlayerLoader = null;
                throw error;
            });
        }

        return this.mediaElementPlayerLoader;
    },

    initPlayer: function () {
        if (this.playerInitializationPromise) {
            return this.playerInitializationPromise;
        }

        var that = this,
            audioSource = document.querySelector('audio source'),
            initialTrackId = audioSource ? audioSource.getAttribute('data-track-initial') : null,
            initialSpeakerAudioId = audioSource ? audioSource.getAttribute('data-speaker-audio-initial') : null,
            initialSoundEffectId = audioSource ? audioSource.getAttribute('data-sound-effect-initial') : null;

        this.playerInitializationPromise = this.ensureMediaElementPlayer().then(function(MediaElementPlayerClass) {
            that.player = new MediaElementPlayerClass('player', {
                features: ['playpause','current','progress','duration','volume'],
                // shows debug errors on screen
                enablePluginDebug: false,
                // remove or reorder to change plugin priority
                plugins: ['flash', 'silverlight'],
                // specify to force MediaElement to use a particular video or audio type
                type: '',
                // // default if the <video width> is not specified
                defaultAudioWidth: that.player.width,
                // default if the <video height> is not specified
                defaultAudioHeight: 140,
                autosizeProgress: false,
                audioVolume: 'vertical',
                // rate in milliseconds for Flash and Silverlight to fire the timeupdate event
                // larger number is less accurate, but less strain on plugin->JavaScript bridge
                timerRate: 1,
                // method that fires when the Flash or Silverlight object is ready
                // // path to Flash and Silverlight plugins
                // pluginPath: '/myjsfiles/',
                // // name of flash file
                // flashName: 'flashmediaelement.swf',
                // // name of silverlight file
                // silverlightName: 'silverlightmediaelement.xap',
                success: function (mediaElement, domObject) {
                    that.setMediaElement(mediaElement);

                    // Get waveform for @track
                    Array.prototype.forEach.call(document.querySelectorAll('span.mejs__time-total, span.mejs__time-loaded, span.mejs__time-current'), function(element) {
                        element.style.display = 'none';
                    });
                    Array.prototype.forEach.call(document.querySelectorAll('.mejs__audio, .mejs__controls'), function(element) {
                        element.style.background = 'none';
                    });

                    var playbackStats = function (event) {
                        var source = (event && event.target) ? event.target : mediaElement;
                        var currentTime = parseFloat(source && source.currentTime);
                        var duration = parseFloat(source && source.duration);

                        return {
                            currentTime: isFinite(currentTime) ? currentTime : 0,
                            duration: isFinite(duration) ? duration : 0
                        };
                    };

                    // add event listener
                    mediaElement.addEventListener('play', function(e) {
                        that.showPlayer();
                        var stats = playbackStats(e);
                        if (that.resetProgressOnNextPlay) {
                            if (that.pendingSeekRatio !== null && stats.duration > 0) {
                                stats.currentTime = stats.duration * that.pendingSeekRatio;
                            } else {
                                stats.currentTime = 0;
                            }
                            that.resetProgressOnNextPlay = false;
                        }
                        that.switchingPlaybackSource = false;
                        var activeTrackData = that.currentPlaybackTrackData || window.AudioPlayer.currentTrackData;
                        legacyEventHub.trigger(
                            'audioPlayerPlay',
                            activeTrackData,
                            stats.currentTime,
                            stats.duration,
                            that.currentPlaybackContext()
                        );
                        that.playing = true;
                    }, false);
                    mediaElement.addEventListener('timeupdate', function(e) {
                        if (that.switchingPlaybackSource) {
                            return;
                        }
                        if (that.ignoreNextTimeUpdateAfterSourceSwitch) {
                            that.ignoreNextTimeUpdateAfterSourceSwitch = false;
                            return;
                        }
                        var stats = playbackStats(e);
                        var activeTrackData = that.currentPlaybackTrackData || window.AudioPlayer.currentTrackData;
                        legacyEventHub.trigger(
                            'audioPlayerTimeUpdate',
                            activeTrackData,
                            stats.currentTime,
                            stats.duration,
                            that.currentPlaybackContext()
                        );
                    }, false);
                    mediaElement.addEventListener('pause', function(e) {
                        var stats = playbackStats(e);
                        var activeTrackData = that.currentPlaybackTrackData || window.AudioPlayer.currentTrackData;
                        legacyEventHub.trigger(
                            'audioPlayerStop',
                            activeTrackData,
                            stats.currentTime,
                            stats.duration,
                            that.currentPlaybackContext()
                        );
                        that.playing = false;
                    }, false);
                    mediaElement.addEventListener('ended', function(e) {
                        var stats = playbackStats(e);
                        var activeTrackData = that.currentPlaybackTrackData || window.AudioPlayer.currentTrackData;
                        legacyEventHub.trigger(
                            'audioPlayerEnded',
                            activeTrackData,
                            stats.currentTime,
                            stats.duration,
                            that.currentPlaybackContext()
                        );
                    }, false);
                    mediaElement.addEventListener('loadedmetadata', function() {
                        that.applyPendingSeekRatio();
                    }, false);
                    mediaElement.addEventListener('canplay', function() {
                        that.applyPendingSeekRatio();
                    }, false);

                    if (initialTrackId) {
                        that.initTrack(initialTrackId);
                    }

                    if (initialSpeakerAudioId) {
                        that.initSpeakerAudio(initialSpeakerAudioId);
                    }

                    if (initialSoundEffectId) {
                        that.initSoundEffect(initialSoundEffectId);
                    }
                },
                // fires when a problem is detected
                error: function () {

                }
            });

            return that.player;
        }).catch(function(error) {
            that.playerInitializationPromise = null;
            throw error;
        });

        return this.playerInitializationPromise;
    },

    setMediaElement: function(mediaElement) {
        if (!mediaElement) {
            return;
        }

        this.mediaElement = mediaElement;
    },

    playableElement: function() {
        if (this.mediaElement && typeof this.mediaElement.setSrc === 'function') {
            return this.mediaElement;
        }

        if (this.player && this.player.media && typeof this.player.media.setSrc === 'function') {
            return this.player.media;
        }

        if (this.player && typeof this.player.setSrc === 'function') {
            return this.player;
        }

        return null;
    },

    setPlayerSrc: function(streamUrl) {
        var playable = this.playableElement();
        if (!playable || !streamUrl) {
            return false;
        }

        playable.setSrc(streamUrl);
        return true;
    },

    setPlaybackContext: function(trackData, trackVariantId, trackVariantData, options) {
        var playbackOptions = options || {};
        this.currentPlaybackTrackData = trackData || null;
        this.currentPlaybackTrackVariantId = trackVariantId || null;
        this.currentPlaybackTrackVariantData = trackVariantData || null;
        this.currentPlaybackSeekRatio = playbackOptions.seekRatio ?? null;
    },

    clampRatio: function(ratio) {
        var parsed = parseFloat(ratio);
        if (!isFinite(parsed)) {
            return 0;
        }

        return Math.max(0, Math.min(1, parsed));
    },

    queueSeekRatio: function(ratio) {
        this.pendingSeekRatio = this.clampRatio(ratio);
    },

    applyPendingSeekRatio: function() {
        var playable = this.playableElement();

        if (!playable || this.pendingSeekRatio === null) {
            return false;
        }

        if (!isFinite(playable.duration) || playable.duration <= 0) {
            return false;
        }

        playable.currentTime = playable.duration * this.pendingSeekRatio;
        this.pendingSeekRatio = null;
        return true;
    },

    pausePlayer: function() {
        var playable = this.playableElement();
        if (playable && typeof playable.pause === 'function') {
            playable.pause();
        }
    },

    playPlayer: function() {
        var playable = this.playableElement();
        if (playable && typeof playable.play === 'function') {
            try {
                var playResult = playable.play();

                if (playResult && typeof playResult.catch === 'function') {
                    playResult.catch(function(error) {
                        console.warn('AudioPlayer play failed', error);
                    });
                }
            } catch (error) {
                console.warn('AudioPlayer play failed', error);
            }
        }
    },

    loadPlayer: function() {
        var playable = this.playableElement();
        if (playable && typeof playable.load === 'function') {
            playable.load();
        }
    },

    initTrack: function (track_id) {
        this.fetchMetadata("/tracks/" + track_id, function(data) {
            if (this.hasUserDrivenPlaybackSelection) {
                return;
            }
            this.currentTrackData = data;
            this.currentTrackVariantId = null;
            this.currentTrackVariantData = null;
            this.updatePlayerWaveform(data);
            this.setPlayerSrc(data.stream_url);
            legacyEventHub.trigger('updateTrackInfo', data);
        }.bind(this));
    },

    initSpeakerAudio: function (speakerAudioId) {
        this.fetchMetadata("/speaker_audios/" + speakerAudioId, function(data) {
            this.currentTrackData = data;
            this.currentTrackVariantId = null;
            this.currentTrackVariantData = null;
            this.updatePlayerWaveform(data);
            this.setPlayerSrc(data.stream_url);
            legacyEventHub.trigger('updateTrackInfo', data);
        }.bind(this));
    },

    initSoundEffect: function (sound_effect_id) {
        this.fetchMetadata("/sound_effects/" + sound_effect_id, function(data) {
            this.currentTrackData = data;
            this.currentTrackVariantId = null;
            this.currentTrackVariantData = null;
            this.updatePlayerWaveform(data);
            this.setPlayerSrc(data.stream_url);
            legacyEventHub.trigger('updateTrackInfo', data);
        }.bind(this));
    },

    initAndPlayTrack: function (trackId) {
        var hasActiveTrackVariant = this.currentTrackVariantId || this.currentPlaybackTrackVariantId;
        this.currentTrackVariantId = null;
        this.currentTrackVariantData = null;

        if (String(this.currentTrackData.id) === String(trackId)) {
            if (hasActiveTrackVariant) {
                this.playParentTrack(trackId);
                return;
            }

            if (this.playing) {
                this.pausePlayer();
            } else {
                this.setPlaybackContext(this.currentTrackData, null, null, { seekRatio: null });
                this.playPlayer()
            }
        } else {
            this.fetchMetadata("/tracks/" + trackId, function(data) {
                this.currentTrackData = data;
                this.updatePlayerWaveform(data);
                this.playTrack(data.stream_url, data);
                legacyEventHub.trigger('updateTrackInfo', data);
            }.bind(this));
        }
    },

    initAndPlayTrackAtRatio: function(trackId, ratio) {
        var hasActiveTrackVariant = this.currentTrackVariantId || this.currentPlaybackTrackVariantId;
        this.currentTrackVariantId = null;
        this.currentTrackVariantData = null;
        this.queueSeekRatio(ratio);

        if (String(this.currentTrackData.id) === String(trackId)) {
            if (hasActiveTrackVariant) {
                this.playParentTrack(trackId);
                return;
            }

            this.applyPendingSeekRatio();

            if (this.playing) {
                return;
            }

            this.setPlaybackContext(this.currentTrackData, null, null, { seekRatio: this.pendingSeekRatio });
            this.playPlayer();
            return;
        }

        this.fetchMetadata("/tracks/" + trackId, function(data) {
            this.currentTrackData = data;
            this.updatePlayerWaveform(data);
            this.playTrack(data.stream_url, data);
            legacyEventHub.trigger('updateTrackInfo', data);
        }.bind(this));
    },

    playParentTrack: function(trackId) {
        if (this.currentTrackData.stream_url) {
            this.updatePlayerWaveform(this.currentTrackData);
            this.playTrack(this.currentTrackData.stream_url, this.currentTrackData);
            legacyEventHub.trigger('updateTrackInfo', this.currentTrackData);
            return;
        }

        this.fetchMetadata("/tracks/" + trackId, function(data) {
            this.currentTrackData = data;
            this.updatePlayerWaveform(data);
            this.playTrack(data.stream_url, data);
            legacyEventHub.trigger('updateTrackInfo', data);
        }.bind(this));
    },

    initAndPlayTrackVariant: function (trackVariantId) {
        if (String(this.currentTrackVariantId) === String(trackVariantId)) {
            if (this.playing) {
                this.pausePlayer();
                return;
            }

            this.setPlaybackContext(this.currentTrackData, this.currentTrackVariantId, this.currentTrackVariantData, { seekRatio: null });
            this.playPlayer();
            return;
        }

        this.fetchMetadata("/track_variants/" + trackVariantId, function(data) {
            this.currentTrackData = data.track_data;
            this.currentTrackVariantId = trackVariantId;
            this.currentTrackVariantData = data.track_variant_data;
            this.updatePlayerWaveform(data.track_variant_data);
            this.playTrack(data.track_variant_data.stream_url, data.track_data, {
                trackVariantId: trackVariantId,
                trackVariantData: data.track_variant_data
            });
            legacyEventHub.trigger('updateTrackVariantInfo', data.track_data, data.track_variant_data);
        }.bind(this));
    },

    initAndPlayTrackVariantAtRatio: function(trackVariantId, ratio) {
        this.queueSeekRatio(ratio);

        if (String(this.currentTrackVariantId) === String(trackVariantId)) {
            this.applyPendingSeekRatio();

            if (this.playing) {
                return;
            }

            this.setPlaybackContext(this.currentTrackData, this.currentTrackVariantId, this.currentTrackVariantData, { seekRatio: this.pendingSeekRatio });
            this.playPlayer();
            return;
        }

        this.fetchMetadata("/track_variants/" + trackVariantId, function(data) {
            this.currentTrackData = data.track_data;
            this.currentTrackVariantId = trackVariantId;
            this.currentTrackVariantData = data.track_variant_data;
            this.updatePlayerWaveform(data.track_variant_data);
            this.playTrack(data.track_variant_data.stream_url, data.track_data, {
                trackVariantId: trackVariantId,
                trackVariantData: data.track_variant_data
            });
            legacyEventHub.trigger('updateTrackVariantInfo', data.track_data, data.track_variant_data);
        }.bind(this));
    },

    initAndPlaySoundEffect: function (soundEffectId) {
        this.currentTrackVariantId = null;
        this.currentTrackVariantData = null;

        if (String(this.currentTrackData.id) === String(soundEffectId)) {
            if (this.playing) {
                this.pausePlayer();
            } else {
                this.setPlaybackContext(this.currentTrackData, null, null, { seekRatio: null });
                this.playPlayer()
            }
        } else {
            this.fetchMetadata("/sound-effects/" + soundEffectId, function(data) {
                this.currentTrackData = data;
                this.updatePlayerWaveform(data);
                this.playTrack(data.stream_url, data);
                legacyEventHub.trigger('updateTrackInfo', data);
            }.bind(this));
        }
    },

    initAndPlaySoundEffectVariant: function (soundEffectVariantId) {
        this.currentTrackVariantId = null;
        this.currentTrackVariantData = null;
        this.fetchMetadata("/sound_effect_variants/" + soundEffectVariantId, function(data) {
            this.currentTrackData = data.sound_effect_data;
            this.updatePlayerWaveform(data.sound_effect_variant_data);
            this.playTrack(data.sound_effect_variant_data.stream_url, data.sound_effect_data);
            legacyEventHub.trigger('updateSoundEffectVariantInfo', data.sound_effect_data, data.sound_effect_variant_data);
        }.bind(this));
    },

    initAndPlaySpeakerAudio: function (speakerAudioId) {
        this.currentTrackVariantId = null;
        this.currentTrackVariantData = null;

        if (String(this.currentTrackData.id) === String(speakerAudioId)) {
            if (this.playing) {
                this.pausePlayer();
            } else {
                this.setPlaybackContext(this.currentTrackData, null, null, { seekRatio: null });
                this.playPlayer()
            }
        } else {
            this.fetchMetadata("/speaker_audios/" + speakerAudioId, function(data) {
                this.currentTrackData = data;
                this.updatePlayerWaveform(data);
                this.playTrack(data.stream_url, data);
                legacyEventHub.trigger('updateTrackInfo', data);
            }.bind(this));
        }
    },

    initAndPlayVoiceRecording: function (voiceRecordingId) {
        this.currentTrackVariantId = null;
        this.currentTrackVariantData = null;
        if (String(this.currentTrackData.id) === String(voiceRecordingId)) {
            if (this.playing) {
                this.pausePlayer();
            } else {
                this.setPlaybackContext(this.currentTrackData, null, null, { seekRatio: null });
                this.playPlayer()
            }
        } else {
            this.fetchMetadata("/voice_recordings/" + voiceRecordingId, function(data) {
                this.currentTrackData = data;
                this.updatePlayerWaveform(data);
                this.playTrack(data.stream_url, data);
                legacyEventHub.trigger('updateTrackInfo', data);
            }.bind(this));
        }
    },

    updatePlayerWaveform: function (data) {
        let urls = [data.waveform_inactive, data.waveform_loaded, data.waveform_played],
            that = this,
            token = ++this.currentWaveformToken,
            remaining = urls.length,
            fallbackDelayMs = 1200;

        if (!urls[0] || !urls[1] || !urls[2]) {
            return;
        }

        function applyWaveformIfCurrent() {
            if (token !== that.currentWaveformToken) {
                return;
            }

            var waveformStyles = [
                ['span.mejs__time-total', data.waveform_inactive],
                ['span.mejs__time-loaded', data.waveform_loaded],
                ['span.mejs__time-current', data.waveform_played]
            ];

            waveformStyles.forEach(function(entry) {
                var element = document.querySelector(entry[0]);
                if (!element) {
                    return;
                }

                element.style.background = 'url(' + entry[1] + ')';
                element.style.backgroundSize = that.player.width + 'px 200px';
                element.style.display = '';
            });
        }

        setTimeout(function() {
            applyWaveformIfCurrent();
        }, fallbackDelayMs);

        urls.forEach(function(url) {
            let img = new Image();
            let done = function() {
                remaining -= 1;
                if (remaining === 0) {
                    applyWaveformIfCurrent();
                }
            };
            img.onload = done;
            img.onerror = done;
            img.src = url;
        });
    },

    playTrack: function (stream_url, trackData, playbackContext) {
        let currentSrc = this.player && this.player.media ? this.player.media.currentSrc : null;
        let nextPlaybackContext = playbackContext || {};

        if (currentSrc !== stream_url) {
            this.resetProgressOnNextPlay = true;
            this.switchingPlaybackSource = true;
            this.ignoreNextTimeUpdateAfterSourceSwitch = this.pendingSeekRatio === null;
            this.pausePlayer();
        }
        if (currentSrc !== stream_url) {
            this.setPlayerSrc(stream_url);
        }
        this.setPlaybackContext(
            trackData || this.currentTrackData,
            nextPlaybackContext.trackVariantId,
            nextPlaybackContext.trackVariantData,
            { seekRatio: nextPlaybackContext.seekRatio ?? this.pendingSeekRatio }
        );
        this.playPlayer();
    },

    currentPlaybackContext: function() {
        return {
            trackData: this.currentPlaybackTrackData || this.currentTrackData,
            trackVariantId: this.currentPlaybackTrackVariantId,
            trackVariantData: this.currentPlaybackTrackVariantData,
            seekRatio: this.currentPlaybackSeekRatio
        };
    },

    firstArg: function(args) {
        return args && args.length > 0 ? args[0] : null;
    },

    secondArg: function(args) {
        return args && args.length > 1 ? args[1] : null;
    },

    eventArgs: function(args) {
        if (Array.isArray(args)) {
            return args;
        }

        return typeof args === 'undefined' ? [] : [args];
    },

    fetchMetadata: function(url, onSuccess) {
        let requestId = ++this.currentRequestId,
            that = this;

        if (this.currentMetadataRequest && typeof this.currentMetadataRequest.abort === 'function') {
            this.currentMetadataRequest.abort();
        }

        this.currentMetadataRequest = typeof AbortController === 'function' ? new AbortController() : null;

        return requestJson(url, { signal: this.currentMetadataRequest?.signal }).then(function(data) {
                if (requestId !== that.currentRequestId) {
                    return;
                }
                onSuccess(data);
        }).catch(function(error) {
            if (error && error.name === 'AbortError') {
                return;
            }
        });
    },

    addEventListeners: function () {
        if (this.listenersBound) {
            return;
        }
        this.listenersBound = true;

        legacyEventHub.on('playTrack', function(args) {
            var eventArgs = this.eventArgs(args);
            var trackId = this.firstArg(eventArgs);
            var previewTrackData = this.secondArg(eventArgs);

            if (trackId) {
                this.hasUserDrivenPlaybackSelection = true;
                legacyEventHub.trigger('audioPlayerWillChangeTrack', {
                    trackId: trackId,
                    trackVariantId: null,
                    trackData: previewTrackData && String(previewTrackData.id) === String(trackId) ? previewTrackData : null,
                    trackVariantData: null,
                    seekRatio: null
                });
                if (previewTrackData && String(previewTrackData.id) === String(trackId)) {
                    this.setPlaybackContext(previewTrackData, null, null, { seekRatio: null });
                    legacyEventHub.trigger('updateTrackInfo', previewTrackData);
                }
                this.initAndPlayTrack(trackId);
            }

            // scroll into view
            // $('html, body').animate({
            //     scrollTop: $('#track-' + trackId).offset().top - 100
            // }, 300);

        }.bind(this));

        legacyEventHub.on('playTrackVariant', function(args) {
            var trackVariantId = this.firstArg(this.eventArgs(args));

            if (trackVariantId) {
                this.hasUserDrivenPlaybackSelection = true;
                legacyEventHub.trigger('audioPlayerWillChangeTrack', {
                    trackId: null,
                    trackVariantId: trackVariantId,
                    trackData: null,
                    trackVariantData: null,
                    seekRatio: null
                });
                this.initAndPlayTrackVariant(trackVariantId);
            }

            // scroll into view
            // $('html, body').animate({
            //     scrollTop: $('#track-' + trackId).offset().top - 100
            // }, 300);

        }.bind(this));

        legacyEventHub.on('playTrackVariantAtRatio', function(args) {
            var payload = this.firstArg(this.eventArgs(args));

            if (!payload || !payload.trackVariantId) {
                return;
            }

            this.hasUserDrivenPlaybackSelection = true;
            legacyEventHub.trigger('audioPlayerWillChangeTrack', {
                trackId: payload.trackId,
                trackVariantId: payload.trackVariantId,
                trackData: null,
                trackVariantData: null,
                seekRatio: payload.ratio
            });
            this.initAndPlayTrackVariantAtRatio(payload.trackVariantId, payload.ratio);
        }.bind(this));

        legacyEventHub.on('playSoundEffect', function(args) {
            var soundEffectId = this.firstArg(this.eventArgs(args));

            if (soundEffectId) {
                this.hasUserDrivenPlaybackSelection = true;
                this.initAndPlaySoundEffect(soundEffectId);
            }

            // scroll into view
            // $('html, body').animate({
            //     scrollTop: $('#track-' + trackId).offset().top - 100
            // }, 300);

        }.bind(this));

        legacyEventHub.on('playSoundEffectVariant', function(args) {
            var soundEffectVariantId = this.firstArg(this.eventArgs(args));

            if (soundEffectVariantId) {
                this.hasUserDrivenPlaybackSelection = true;
                this.initAndPlaySoundEffectVariant(soundEffectVariantId);
            }

            // scroll into view
            // $('html, body').animate({
            //     scrollTop: $('#track-' + trackId).offset().top - 100
            // }, 300);

        }.bind(this));

        legacyEventHub.on('playSpeakerAudio', function(args) {
            var speakerAudioId = this.firstArg(this.eventArgs(args));

            if (speakerAudioId) {
                this.hasUserDrivenPlaybackSelection = true;
                this.initAndPlaySpeakerAudio(speakerAudioId);
            }

            // // scroll into view
            // $('html, body').animate({
            //     scrollTop: $('[data-play-speaker-audio-id=' + speakerAudioId + ']').offset().top - 100
            // }, 300);

        }.bind(this));

        legacyEventHub.on('playVoiceRecording', function(args) {
            var voiceRecordingId = this.firstArg(this.eventArgs(args));

            if (voiceRecordingId) {
                this.hasUserDrivenPlaybackSelection = true;
                this.initAndPlayVoiceRecording(voiceRecordingId);
            }

            // // scroll into view
            // $('html, body').animate({
            //     scrollTop: $('[data-play-speaker-audio-id=' + speakerAudioId + ']').offset().top - 100
            // }, 300);

        }.bind(this));

        legacyEventHub.on('playTrackAtRatio', function(args) {
            var payload = this.firstArg(this.eventArgs(args));

            if (!payload || !payload.trackId) {
                return;
            }

            this.hasUserDrivenPlaybackSelection = true;
            if (payload.trackData && String(payload.trackData.id) === String(payload.trackId)) {
                legacyEventHub.trigger('audioPlayerWillChangeTrack', {
                    trackId: payload.trackId,
                    trackVariantId: null,
                    trackData: payload.trackData,
                    trackVariantData: null,
                    seekRatio: payload.ratio
                });
                this.setPlaybackContext(payload.trackData, null, null, { seekRatio: payload.ratio });
                legacyEventHub.trigger('updateTrackInfo', payload.trackData);
            } else {
                legacyEventHub.trigger('audioPlayerWillChangeTrack', {
                    trackId: payload.trackId,
                    trackVariantId: null,
                    trackData: null,
                    trackVariantData: null,
                    seekRatio: payload.ratio
                });
            }

            this.initAndPlayTrackAtRatio(payload.trackId, payload.ratio);
        }.bind(this));
    }
};

window.AudioPlayer = AudioPlayer;

export default AudioPlayer;
